import { useCallback, useEffect, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useAgentChat } from '@/lib/useAgentChat';
import { useStableApi } from '@/lib/api';
import ChatPanel from './ChatPanel';

/**
 * Floating chat dock (PRD §3.8) — mounted once in DefaultLayout so a launcher button
 * exists on every authenticated page, mirroring the reference system's AgentChatDock
 * (a corner panel, not a dedicated route). The full-page Chat.jsx remains as the
 * "page" mounting mode for the same underlying chat (both use useAgentChat()/ChatPanel).
 *
 * `openRequest` (from ChatDockContext) lets other pages — e.g. the Agents list's
 * per-row chat button — open the dock pre-set to a specific agent.
 *
 * The dock is also the courier for the two page-scoped channels ChatDockContext defines:
 * it reads the current page's screen context at send time, and after a turn lands it looks
 * through that turn's traces for a tool the page knows how to act on.
 */

/** Tool results a page can apply. Whitelisted rather than forwarding every tool_output —
 *  the page applies these to real state, so it must be a closed set, not "whatever ran". */
const PROPOSAL_TOOLS = new Set(['propose_workflow_graph', 'propose_mock_spec']);

/**
 * Tool results are double-encoded: every tool in this app returns a JSON *string*, and
 * recordTrace stringifies that again before storing it. The traces endpoint parses once,
 * which yields the inner string rather than the object — so one more parse is needed here.
 * Without it every proposal silently reads as malformed.
 */
function decodeToolOutput(output) {
  if (typeof output !== 'string') return output;
  try {
    return JSON.parse(output);
  } catch {
    return null;
  }
}

export default function AgentChatDock({ openRequest, getScreenContext, publishProposal }) {
  const [open, setOpen] = useState(false);
  // Local state, unlike the embed widget's equivalent: the dock owns its own wrapper, so
  // there is no host page to ask and no postMessage round-trip.
  const [expanded, setExpanded] = useState(false);
  const { fetchApi } = useStableApi();

  /** Pull the finished turn's tool results and hand any proposal to the page. */
  const onTurnComplete = useCallback(async ({ sessionId, messageId }) => {
    if (!publishProposal) return;
    try {
      const { traces } = await fetchApi('agentSessionTraces', { sessionId, messageId });
      // Last wins: within one turn the model may refine its answer across several calls.
      const hit = [...(traces || [])].reverse()
        .find((t) => PROPOSAL_TOOLS.has(t.tool_name) && t.status === 'ok' && t.tool_output);
      if (hit) publishProposal({ tool: hit.tool_name, output: decodeToolOutput(hit.tool_output), at: Date.now() });
    } catch {
      // A proposal the page never receives just means no banner — never break the chat.
    }
  }, [publishProposal]);

  const chat = useAgentChat({ getContext: getScreenContext, onTurnComplete });

  useEffect(() => {
    if (open) chat.loadAgents();
  }, [open]);

  useEffect(() => {
    if (!openRequest) return;
    setOpen(true);
    chat.loadAgents();
    if (openRequest.agentKey) chat.setAgentKey(openRequest.agentKey);
  }, [openRequest]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open chat"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary-600 text-white
          shadow-lg flex items-center justify-center hover:bg-primary-700 transition-colors"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  // Clamped against the viewport rather than sized in vh alone, so the dock never grows
  // past the 24px inset it is anchored by.
  const size = expanded
    ? 'w-[min(760px,calc(100vw-48px))] h-[min(90vh,calc(100vh-48px))]'
    : 'w-[360px] h-[520px] max-h-[calc(100vh-48px)]';

  return (
    <div className={`fixed bottom-6 right-6 z-50 ${size} bg-[var(--app-surface)] border border-[var(--app-border)] rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-[width,height] duration-200`}>
      {chat.loadError ? (
        <>
          <div className="flex items-center justify-end px-3 py-2 border-b border-[var(--app-border)]">
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" className="p-1.5 rounded-lg text-secondary-500 hover:bg-[var(--app-border)]/40">
              <X size={16} />
            </button>
          </div>
          <div className="p-3 text-sm text-error-600">{chat.loadError}</div>
        </>
      ) : (
        <ChatPanel
          agents={chat.agents}
          agentKey={chat.agentKey}
          onChangeAgent={chat.setAgentKey}
          sessionId={chat.sessionId}
          messages={chat.messages}
          sending={chat.sending}
          pendingJob={chat.pendingJob}
          onSend={chat.send}
          onAnswerQuestion={chat.answerQuestion}
          onUploadAttachment={chat.uploadAttachment}
          onClearChat={chat.clearChat}
          approvalMode={chat.approvalMode}
          onToggleApprovalMode={chat.setApprovalMode}
          onClose={() => setOpen(false)}
          expanded={expanded}
          onToggleExpanded={() => setExpanded((v) => !v)}
        />
      )}
    </div>
  );
}
